import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, X } from "lucide-react";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { useIsMobile } from "@/hooks/use-mobile";
import { parseChecklistSteps } from "./checklistSteps";
import {
  addTemplateSteps,
  nativeSelectClass,
  type ChecklistTemplate,
  type TemplateItem,
  fullName,
  useProcedures,
  useTaskAreas,
  useTeam,
} from "./taskData";

// One checklist template (task 1.3, D-030): name, description, default area,
// procedure and steps. The whole team edits templates (D-018). Fields are
// saved one at a time; a change never touches the tasks already created.
export const TemplateEditor = ({
  template,
  onOpenChange,
}: {
  template: ChecklistTemplate | null;
  onOpenChange: (open: boolean) => void;
}) => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  return (
    <Sheet open={template != null} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className="max-h-[95dvh] overflow-y-auto"
      >
        <SheetHeader className="p-4 pb-0">
          <SheetTitle>{translate("tenax.tasks.templates.edit")}</SheetTitle>
        </SheetHeader>
        {template && <TemplateForm template={template} />}
      </SheetContent>
    </Sheet>
  );
};

const TemplateForm = ({ template }: { template: ChecklistTemplate }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: areas = [] } = useTaskAreas();
  const { data: procedures = [] } = useProcedures();
  const { data: team = [] } = useTeam();
  const { data: items = [] } = useGetList<TemplateItem>(
    "checklist_template_items",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "position", order: "ASC" },
      filter: { template_id: template.id },
    },
  );
  const [draft, setDraft] = useState("");

  const run = async (action: () => Promise<unknown>) => {
    try {
      await action();
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await queryClient.invalidateQueries({ queryKey: ["checklist_templates"] });
    await queryClient.invalidateQueries({
      queryKey: ["checklist_template_items"],
    });
  };

  const save = (data: Partial<ChecklistTemplate>) =>
    run(() =>
      dataProvider.update("checklist_templates", {
        id: template.id,
        data,
        previousData: template,
      }),
    );

  const updateItem = (item: TemplateItem, data: Partial<TemplateItem>) =>
    dataProvider.update("checklist_template_items", {
      id: item.id,
      data,
      previousData: item,
    });

  const move = (index: number, offset: -1 | 1) => {
    const item = items[index];
    const other = items[index + offset];
    if (!other) return;
    return run(async () => {
      await updateItem(item, { position: other.position });
      await updateItem(other, { position: item.position });
    });
  };

  const add = (event: React.FormEvent) => {
    event.preventDefault();
    const labels = parseChecklistSteps(draft);
    if (labels.length === 0) return;
    const next = Math.max(0, ...items.map((item) => item.position)) + 1;
    setDraft("");
    return run(() => addTemplateSteps(template.id, labels, next));
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="template-name">
          {translate("tenax.tasks.templates.name")}
        </Label>
        <Input
          id="template-name"
          key={`name-${template.name}`}
          defaultValue={template.name}
          onBlur={(event) => {
            const value = event.target.value.trim();
            if (value && value !== template.name) save({ name: value });
          }}
        />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="template-description">
          {translate("tenax.tasks.templates.description")}
        </Label>
        <Textarea
          id="template-description"
          key={`description-${template.description ?? ""}`}
          rows={2}
          defaultValue={template.description ?? ""}
          onBlur={(event) => {
            if (event.target.value !== (template.description ?? "")) {
              save({ description: event.target.value });
            }
          }}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="template-area">
            {translate("tenax.tasks.templates.default_area")}
          </Label>
          <select
            id="template-area"
            className={nativeSelectClass}
            value={template.area_id ?? ""}
            onChange={(event) =>
              save({
                area_id: event.target.value ? Number(event.target.value) : null,
              })
            }
          >
            <option value="">{translate("tenax.tasks.fields.no_area")}</option>
            {areas
              .filter((area) => area.active || area.id === template.area_id)
              .map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="template-procedure">
            {translate("tenax.tasks.templates.procedure")}
          </Label>
          <select
            id="template-procedure"
            className={nativeSelectClass}
            value={template.procedure_id ?? ""}
            onChange={(event) =>
              save({
                procedure_id: event.target.value
                  ? Number(event.target.value)
                  : null,
              })
            }
          >
            <option value="">
              {translate("tenax.tasks.templates.no_procedure")}
            </option>
            {procedures.map((procedure) => (
              <option key={procedure.id} value={procedure.id}>
                {procedure.title}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-2 flex flex-col gap-1.5">
          <Label htmlFor="template-assignee">
            {translate("tenax.tasks.templates.default_assignee")}
          </Label>
          <select
            id="template-assignee"
            className={nativeSelectClass}
            value={template.default_assignee_id ?? ""}
            onChange={(event) =>
              save({
                default_assignee_id: event.target.value
                  ? Number(event.target.value)
                  : null,
              })
            }
          >
            <option value="">
              {translate("tenax.tasks.templates.no_assignee")}
            </option>
            {team.map((member) => (
              <option key={member.id} value={member.id}>
                {fullName(member)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <section className="flex flex-col gap-2">
        <h3 className="font-semibold">
          {translate("tenax.tasks.templates.steps")}
        </h3>
        <ul className="flex flex-col gap-1">
          {items.map((item, index) => (
            <li key={item.id} className="flex items-center gap-1">
              <Input
                key={`${item.id}-${item.label}`}
                defaultValue={item.label}
                aria-label={translate("tenax.tasks.fields.steps")}
                onBlur={(event) => {
                  const value = event.target.value.trim();
                  if (value && value !== item.label) {
                    run(() => updateItem(item, { label: value }));
                  }
                }}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                aria-label={translate("tenax.tasks.checklist.move_up")}
              >
                <ArrowUp />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={index === items.length - 1}
                onClick={() => move(index, 1)}
                aria-label={translate("tenax.tasks.checklist.move_down")}
              >
                <ArrowDown />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() =>
                  run(() =>
                    dataProvider.delete("checklist_template_items", {
                      id: item.id,
                      previousData: item,
                    }),
                  )
                }
                aria-label={translate("tenax.tasks.checklist.remove")}
              >
                <X />
              </Button>
            </li>
          ))}
        </ul>
        <form onSubmit={add} className="flex flex-col gap-2">
          <Textarea
            rows={3}
            value={draft}
            placeholder={translate("tenax.tasks.checklist.add_placeholder")}
            aria-label={translate("tenax.tasks.checklist.add")}
            onChange={(event) => setDraft(event.target.value)}
          />
          <Button type="submit" variant="outline" disabled={!draft.trim()}>
            {translate("tenax.tasks.checklist.add")}
          </Button>
        </form>
      </section>

      <Button
        variant="outline"
        onClick={() => save({ active: !template.active })}
      >
        {translate(
          template.active
            ? "tenax.tasks.templates.turn_off"
            : "tenax.tasks.templates.turn_on",
        )}
      </Button>
    </div>
  );
};
